import { useCallback, useState } from "react";

import { useT } from "../../../shared/i18n";
import {
  Badge,
  Banner,
  Button,
  Card,
  ListItem,
  Skeleton,
  Table,
} from "../../../shared/ui";
import { Group } from "../Group";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { formatNumber, formatPercent } from "./format";
import {
  ObservabilityDenied,
  type LoadData,
  type ObservabilityApi,
  type OverviewData,
  type QueuesData,
} from "./observabilityApi";
import { usePolling } from "./usePolling";

const WINDOW_HOURS = 24;

interface Snapshot {
  overview: OverviewData;
  queues: QueuesData;
  load: LoadData;
}

type Status = "loading" | "ready" | "failed" | "denied";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col min-w-0 rounded-[18px] bg-raised p-3.5 shadow-card">
      <span className="text-[12px] leading-4 text-fg-muted">{label}</span>
      <span className="text-title text-fg">{value}</span>
    </div>
  );
}

/**
 * Live load of the platform: job overview, queues, load by job class and the
 * vLLM gauges (OBS-006). Without `observability.read` it says so instead of hiding.
 */
export function LoadQueuesPanel({ api }: { api: ObservabilityApi }) {
  const t = useT();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const reportExpired = useReportSessionExpired();

  const refresh = useCallback(() => {
    Promise.all([api.overview(WINDOW_HOURS), api.queues(), api.load()])
      .then(([overview, queues, load]) => {
        setSnapshot({ overview, queues, load });
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (isSessionExpired(error)) reportExpired();
        if (error instanceof ObservabilityDenied) setStatus("denied");
        else setStatus((current) => (current === "ready" ? current : "failed"));
      });
  }, [api, reportExpired]);
  usePolling(refresh);

  const title = t("admin.load.title");
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h2 className="text-title text-fg">{title}</h2>
      {status === "denied" && (
        <PermissionDenied message={t("admin.denied.load")} />
      )}
      {status === "loading" && (
        <div aria-busy="true">
          <Skeleton height={64} />
          <span className="sr-only">{t("admin.load.loading")}</span>
        </div>
      )}
      {status === "failed" && (
        <Banner
          tone="error"
          action={
            <Button variant="secondary" onClick={refresh}>
              {t("admin.load.retry")}
            </Button>
          }
        >
          {t("admin.load.failed")}
        </Banner>
      )}
      {snapshot !== null && <LoadBody snapshot={snapshot} />}
    </section>
  );
}

function Unavailable() {
  const t = useT();
  return <Banner tone="warning">{t("admin.load.unavailable")}</Banner>;
}

function LoadBody({ snapshot }: { snapshot: Snapshot }) {
  const t = useT();
  const { overview, queues, load } = snapshot;
  const totalJobs = Object.values(overview.jobs_by_state).reduce(
    (sum, count) => sum + count,
    0,
  );
  const queuesDown = queues.degraded === "unavailable";
  const loadDown = load.degraded === "unavailable";
  const waiting: [string, number][] = [
    [t("admin.load.waitingAccepted"), queues.jobs_waiting.accepted],
    [t("admin.load.waitingQueued"), queues.jobs_waiting.queued],
    [t("admin.load.waitingWaiting"), queues.jobs_waiting.waiting],
  ];
  return (
    <>
      {(queuesDown || loadDown) && (
        <Banner tone="info">{t("admin.load.notConfigured")}</Banner>
      )}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        <Stat label={t("admin.load.jobsTotal")} value={String(totalJobs)} />
        <Stat
          label={t("admin.load.failureRate")}
          value={formatPercent(overview.failure_rate)}
        />
        <Stat
          label={t("admin.load.durationP50")}
          value={formatNumber(overview.duration_seconds.p50)}
        />
        <Stat
          label={t("admin.load.durationP95")}
          value={formatNumber(overview.duration_seconds.p95)}
        />
        <Stat
          label={t("admin.load.hold")}
          value={`${overview.hold.state} (${String(overview.hold.waiting_jobs)})`}
        />
        <Stat
          label={t("admin.load.outboxLag")}
          value={queuesDown ? "-" : formatNumber(queues.outbox_lag_seconds)}
        />
      </div>
      <ul className="flex flex-wrap gap-2">
        {Object.entries(overview.jobs_by_state).map(([state, count]) => (
          <li key={state}>
            <Badge tone="neutral">{`${state}: ${String(count)}`}</Badge>
          </li>
        ))}
      </ul>

      <Group title={t("admin.load.queues")}>
        {queuesDown ? (
          <Unavailable />
        ) : (
          <>
            <Table
              caption={t("admin.load.queues")}
              rows={queues.queues}
              rowKey={(row) => row.queue}
              empty={t("admin.load.queuesEmpty")}
              columns={[
                {
                  id: "queue",
                  header: t("admin.load.queue"),
                  cell: (row) => row.queue,
                },
                {
                  id: "ready",
                  header: t("admin.load.ready"),
                  cell: (row) => row.ready,
                },
                {
                  id: "consumers",
                  header: t("admin.load.consumers"),
                  cell: (row) => row.consumers,
                },
              ]}
            />
            <Card flush>
              <ul>
                {waiting.map(([label, count]) => (
                  <li key={label}>
                    <ListItem
                      primary={label}
                      trailing={<span className="pr-4">{count}</span>}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </Group>

      <Group title={t("admin.load.jobClasses")}>
        {loadDown ? (
          <Unavailable />
        ) : (
          <Table
            caption={t("admin.load.jobClasses")}
            rows={load.job_classes}
            rowKey={(row) => row.job_class}
            empty={t("admin.load.jobClassesEmpty")}
            columns={[
              {
                id: "class",
                header: t("admin.load.jobClass"),
                cell: (row) => row.job_class,
              },
              {
                id: "busy",
                header: t("admin.load.busy"),
                cell: (row) => row.busy,
              },
              {
                id: "wait",
                header: t("admin.load.waitP95"),
                cell: (row) => formatNumber(row.wait_p95_seconds),
              },
            ]}
          />
        )}
      </Group>

      <Group title={t("admin.load.vllm")}>
        {loadDown ? (
          <Unavailable />
        ) : (
          <div className="grid grid-cols-3 gap-2.5">
            <Stat
              label={t("admin.load.vllmRunning")}
              value={formatNumber(load.vllm.running)}
            />
            <Stat
              label={t("admin.load.vllmWaiting")}
              value={formatNumber(load.vllm.waiting)}
            />
            <Stat
              label={t("admin.load.vllmKv")}
              value={formatPercent(load.vllm.kv_cache_usage)}
            />
          </div>
        )}
      </Group>
    </>
  );
}
